import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon, GlobeAltIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { articleService } from '../../api/services';
import { carbonApi } from '../../api/carbon';
import {
  Article,
  TauxTva,
  Unite,
  TVA_LABELS,
  UNITE_LABELS,
  EmissionScope,
  EmissionCategory,
  EMISSION_SCOPE_LABELS,
  EMISSION_CATEGORY_LABELS,
} from '../../types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import ArticleSuppliers from './ArticleSuppliers';
import toast from 'react-hot-toast';

const SERVICE_UNITES: ReadonlySet<Unite> = new Set([
  Unite.HEURE,
  Unite.JOUR,
  Unite.FORFAIT,
  Unite.SERVICE,
]);

export function ArticleForm() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Partial<Article>>({
    defaultValues: {
      tauxTva: TauxTva.TVA_19,
      unite: Unite.PIECE,
      fodec: false,
      actif: true,
      prixUnitaireHT: 0,
    },
  });

  const watchedUnite = watch('unite');
  const isService = !!watchedUnite && SERVICE_UNITES.has(watchedUnite as Unite);

  // Force stock to 0 when the unite is a service so a previously-entered stock
  // value can't sneak into the PUT payload after toggling to a service unit.
  useEffect(() => {
    if (isService) {
      setValue('stockActuel', 0);
      setValue('stockMinimum', 0);
    }
  }, [isService, setValue]);

  useEffect(() => {
    if (isEdit && id) {
      loadArticle(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadArticle = async (articleId: number) => {
    try {
      setLoading(true);
      const data = await articleService.getById(articleId);
      reset(data);
    } catch (error) {
      toast.error(t.messages.loadError);
      navigate('/articles');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: Partial<Article>) => {
    try {
      setSaving(true);
      if (isEdit && id) {
        await articleService.update(parseInt(id), data);
      } else {
        await articleService.create(data);
      }
      toast.success(t.messages.saveSuccess);
      navigate('/articles');
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setSaving(false);
    }
  };

  // Ask the Green Target (GTS) platform to suggest an emission factor + its
  // classification (scope/category) from the article's designation and unit.
  const handleSuggestFactor = async () => {
    const designation = (watch('designation') ?? '').trim();
    if (designation.length < 2) {
      toast.error('Renseignez d’abord la désignation de l’article.');
      return;
    }
    try {
      setSuggesting(true);
      const s = await carbonApi.suggestFactor(designation, watch('unite') as Unite | undefined);
      if (s.enabled === false) {
        toast.error('Intégration Green Target désactivée côté serveur.');
        return;
      }
      // The backend proxy degrades gracefully: a GTS-side failure still returns
      // 200 with an `error` field. Surface it distinctly from a genuine "no match".
      if (s.error) {
        toast.error(
          s.reason === 'timeout'
            ? 'Green Target a mis trop de temps à répondre — réessayez.'
            : 'Plateforme Green Target injoignable — réessayez plus tard.',
        );
        return;
      }
      if (!s.matched) {
        toast('Aucun facteur trouvé pour cet article — saisie manuelle possible.', { icon: 'ℹ️' });
        return;
      }
      if (s.emissionFactor != null) {
        setValue('emissionFactor', s.emissionFactor, { shouldDirty: true });
      }
      if (s.scope) setValue('emissionScope', s.scope as EmissionScope, { shouldDirty: true });
      if (s.category) setValue('emissionCategory', s.category as EmissionCategory, { shouldDirty: true });
      toast.success(
        s.rationale ? `Facteur suggéré par GTS : ${s.rationale}` : 'Facteur suggéré par GTS.',
      );
    } catch (e) {
      // Reveal the real cause instead of a generic message: a 401 here means the
      // request reached the backend but carried no active company (no tenant
      // context), which is distinct from the server being unreachable.
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        toast.error('Aucune société active — sélectionnez une société puis réessayez.');
      } else if (status) {
        toast.error(`Erreur Green Target (HTTP ${status}).`);
      } else {
        toast.error('Serveur injoignable — vérifiez votre connexion réseau.');
      }
    } finally {
      setSuggesting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/articles')} className="btn-outline p-2">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="page-title">
            {isEdit ? t.articles.editArticle : t.articles.newArticle}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informations de l'article
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t.common.code} *</label>
              <input
                {...register('code', { required: t.validation.required })}
                className={`input-field ${errors.code ? 'input-error' : ''}`}
                placeholder="ART001"
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.common.reference}</label>
              <input
                {...register('reference')}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">{t.common.designation} *</label>
              <input
                {...register('designation', {
                  required: t.validation.required,
                  minLength: { value: 2, message: 'Minimum 2 caractères' },
                })}
                className={`input-field ${errors.designation ? 'input-error' : ''}`}
              />
              {errors.designation && (
                <p className="mt-1 text-sm text-red-600">{errors.designation.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="label">{t.common.description}</label>
              <textarea
                {...register('description')}
                rows={2}
                className="input-field"
              />
            </div>

            <div>
              <label className="label">{t.articles.categorie}</label>
              <input
                {...register('categorie')}
                className="input-field"
                placeholder="Catégorie"
              />
            </div>

            <div>
              <label className="label">{t.articles.codeBarres}</label>
              <input
                {...register('codeBarres')}
                className="input-field font-mono"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Prix et fiscalité
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="label">{t.articles.prixUnitaireHT} *</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.001"
                  {...register('prixUnitaireHT', {
                    required: t.validation.required,
                    valueAsNumber: true,
                    min: { value: 0, message: t.validation.positiveNumber },
                  })}
                  className={`input-field pr-16 ${errors.prixUnitaireHT ? 'input-error' : ''}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  TND
                </span>
              </div>
              {errors.prixUnitaireHT && (
                <p className="mt-1 text-sm text-red-600">{errors.prixUnitaireHT.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.articles.tauxTva} *</label>
              <select
                {...register('tauxTva', { required: t.validation.required })}
                className={`input-field ${errors.tauxTva ? 'input-error' : ''}`}
              >
                {Object.entries(TVA_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">{t.articles.unite} *</label>
              <select
                {...register('unite', { required: t.validation.required })}
                className={`input-field ${errors.unite ? 'input-error' : ''}`}
              >
                {Object.entries(UNITE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('fodec')}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{t.articles.fodec}</span>
            </label>
            <p className="ml-6 text-xs text-gray-500 mt-1">
              Fonds de Développement de la Compétitivité (1% du montant HT)
            </p>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <GlobeAltIcon className="h-5 w-5 text-green-600" />
              Empreinte carbone
            </h2>
            <button
              type="button"
              onClick={handleSuggestFactor}
              disabled={suggesting}
              className="btn-outline flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <SparklesIcon className={`h-4 w-4 ${suggesting ? 'animate-pulse' : ''}`} />
              {suggesting ? 'Suggestion…' : 'Suggérer via GTS'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Le facteur d’émission (kg CO₂e par unité) sert à calculer l’empreinte de chaque
            ligne (quantité × facteur) et à synchroniser les achats avec la plateforme Green Target.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Facteur d’émission</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.0001"
                  min={0}
                  {...register('emissionFactor', {
                    setValueAs: (v) =>
                      v === '' || v === null || v === undefined ? null : Number(v),
                  })}
                  className="input-field pr-20"
                  placeholder="0.0000"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                  kg CO₂e
                </span>
              </div>
            </div>

            <div>
              <label className="label">Périmètre (scope)</label>
              <select
                {...register('emissionScope', { setValueAs: (v) => v || null })}
                className="input-field"
              >
                <option value="">—</option>
                {Object.values(EmissionScope).map((s) => (
                  <option key={s} value={s}>
                    {EMISSION_SCOPE_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Catégorie</label>
              <select
                {...register('emissionCategory', { setValueAs: (v) => v || null })}
                className="input-field"
              >
                <option value="">—</option>
                {Object.values(EmissionCategory).map((c) => (
                  <option key={c} value={c}>
                    {EMISSION_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Stock</h2>
          {isService ? (
            <p className="text-sm text-gray-500">
              Aucun stock à suivre pour une prestation de service ({UNITE_LABELS[watchedUnite as Unite]}).
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">{t.articles.stockActuel}</label>
                <input
                  type="number"
                  {...register('stockActuel', { valueAsNumber: true })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="label">{t.articles.stockMinimum}</label>
                <input
                  type="number"
                  {...register('stockMinimum', { valueAsNumber: true })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="label">Délai d’appro (jours)</label>
                <input
                  type="number"
                  min={0}
                  {...register('delaiApprovisionnement', { valueAsNumber: true })}
                  className="input-field"
                  placeholder="Défaut société"
                />
              </div>

              {isEdit && (
                <div>
                  <label className="label">CMUP (coût moyen pondéré)</label>
                  <input
                    type="number"
                    value={watch('cmup') ?? 0}
                    readOnly
                    disabled
                    className="input-field bg-gray-50 text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Recalculé automatiquement à chaque entrée d’achat.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('actif')}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{t.articles.actif}</span>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/articles')}
            className="btn-outline"
          >
            {t.common.cancel}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary"
          >
            {saving ? t.common.loading : t.common.save}
          </button>
        </div>
      </form>

      {isEdit && id && <ArticleSuppliers articleId={Number(id)} />}
    </div>
  );
}

export default ArticleForm;
